<script setup lang="ts">
import { computed, ref } from "vue";
import { CalendarDays, Rows3 } from "lucide-vue-next";

const props = defineProps<{
  tasks: any[];
  selectedDate: string;
}>();
const emit = defineEmits<{
  select: [date: string];
}>();

const mode = ref<"week" | "month">("week");
const cursor = ref(new Date());

function dateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}
function taskCount(date: string) {
  return props.tasks.filter((task) => {
    const start = task.planned_start_at ? String(task.planned_start_at).slice(0, 10) : "";
    const end = task.planned_end_at ? String(task.planned_end_at).slice(0, 10) : "";
    return start === date || end === date || (start && end && date >= start && date <= end);
  }).length;
}

const monthTitle = computed(() => `${cursor.value.getFullYear()}年 ${cursor.value.getMonth() + 1}月`);
const weekDays = computed(() => {
  const current = cursor.value;
  const mondayOffset = (current.getDay() + 6) % 7;
  const monday = addDays(current, -mondayOffset);
  return Array.from({ length: 7 }, (_, index) => addDays(monday, index));
});
const monthDays = computed(() => {
  const year = cursor.value.getFullYear();
  const month = cursor.value.getMonth();
  const first = new Date(year, month, 1);
  const start = addDays(first, -((first.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
});
const weekdays = ["一", "二", "三", "四", "五", "六", "日"];
const today = dateKey(new Date());

function selectDate(date: Date) {
  const key = dateKey(date);
  cursor.value = date;
  emit("select", props.selectedDate === key ? "" : key);
}
</script>

<template>
  <section class="mobile-calendar">
    <header>
      <div><small>任务日历</small><b>{{ monthTitle }}</b></div>
      <div class="calendar-mode" role="group" aria-label="日历显示方式">
        <button :class="{ active: mode === 'week' }" type="button" aria-label="周视图" @click="mode = 'week'"><Rows3 :size="17" /></button>
        <button :class="{ active: mode === 'month' }" type="button" aria-label="月视图" @click="mode = 'month'"><CalendarDays :size="17" /></button>
      </div>
    </header>

    <div v-if="mode === 'week'" class="week-grid">
      <button v-for="(day, index) in weekDays" :key="dateKey(day)" :class="{ active: selectedDate === dateKey(day), today: today === dateKey(day) }" type="button" @click="selectDate(day)"><small>周{{ weekdays[index] }}</small><b>{{ day.getDate() }}</b><i v-if="taskCount(dateKey(day))">{{ taskCount(dateKey(day)) }}</i></button>
    </div>
    <div v-else class="month-grid">
      <small v-for="weekday in weekdays" :key="weekday">{{ weekday }}</small>
      <button v-for="day in monthDays" :key="dateKey(day)" :class="{ muted: day.getMonth() !== cursor.getMonth(), active: selectedDate === dateKey(day), today: today === dateKey(day) }" type="button" @click="selectDate(day)"><span>{{ day.getDate() }}</span><i v-if="taskCount(dateKey(day))"></i></button>
    </div>
    <button v-if="selectedDate" class="clear-date" type="button" @click="emit('select', '')">清除日期筛选</button>
  </section>
</template>

<style scoped>
.mobile-calendar{display:none}
@media(max-width:700px){
  .mobile-calendar{display:block;padding:15px 14px 12px;background:#203a34;color:#fff}
  .mobile-calendar header{display:flex;align-items:center;justify-content:space-between;margin-bottom:13px}
  .mobile-calendar header>div:first-child{display:grid;gap:2px}.mobile-calendar header small{color:rgba(255,255,255,.68);font-size:11px}.mobile-calendar header b{font-size:17px;letter-spacing:0}
  .calendar-mode{display:flex;padding:2px;border:1px solid rgba(255,255,255,.18);border-radius:6px;background:rgba(255,255,255,.08)}.calendar-mode button{display:grid;width:34px;height:30px;place-items:center;border:0;border-radius:4px;background:transparent;color:rgba(255,255,255,.7)}.calendar-mode button.active{background:#fff;color:#203a34}
  .week-grid{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:3px}.week-grid button{position:relative;display:grid;min-width:0;min-height:60px;place-items:center;align-content:center;gap:3px;border:0;border-radius:6px;background:transparent;color:#fff}.week-grid button small{font-size:10px}.week-grid button b{font-size:16px}.week-grid button.today:not(.active){box-shadow:inset 0 0 0 1px rgba(255,255,255,.45)}.week-grid button.active{background:#f36a16}.week-grid button i{position:absolute;bottom:4px;min-width:15px;height:15px;border-radius:8px;background:#fff;color:#203a34;font:normal 9px/15px sans-serif}
  .month-grid{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:2px}.month-grid>small{text-align:center}.month-grid button{position:relative;display:grid;min-height:36px;place-items:center;border:0;border-radius:5px;background:transparent;color:#fff}.month-grid button.muted{opacity:.34}.month-grid button.active{background:#f36a16}.month-grid button.today:not(.active){box-shadow:inset 0 0 0 1px rgba(255,255,255,.45)}.month-grid button i{position:absolute;bottom:3px;width:4px;height:4px;border-radius:50%;background:#ffb98f}
  .clear-date{display:block;margin:8px auto -5px;border:0;background:transparent;color:rgba(255,255,255,.72);font-size:11px}
}
</style>
